import * as process from "node:process";

import { DateTime } from "luxon";

import {
  IPrintersLabel,
  IPrintersPrinter,
  IUnusedPrinters, IUsedLabel, IUsedPrinter,
} from "@/types/printers.js";
import type { requestParams } from "@/types/requests.ts";
import type { TableRow, TableHeader } from "@/types/table.ts";

export const formatCommaWithDecimals = (amount: number | string) => {
  if (amount === null || amount === undefined || amount === "") return "";

  const numericAmount
        = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "N/A";

  return numericAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

export const formatCurrency = (
  amount: number,
  isNegativeSymbolBeforeCurrency: boolean = true,
  showDecimals: boolean = false,
) => {
  if (amount === null || amount === undefined) return "$0.00";
  const sign = amount < 0 ? "-" : "";
  const absAmount = Math.abs(amount);

  const formattedAmount = absAmount.toLocaleString(undefined, {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  return (
    (isNegativeSymbolBeforeCurrency && sign ? `${sign}$` : "$")
    + formattedAmount
    + (!isNegativeSymbolBeforeCurrency && sign ? `${sign}$` : "")
  );
};

export const formatCurrencyWithTwoDecimals = (
  amount: number,
  isNegativeSymbolBeforeCurrency: boolean = true,
) => {
  return formatCurrency(amount, isNegativeSymbolBeforeCurrency, true);
};

export const formatComma = (amount: number, decimal?: number) => {
  return amount?.toLocaleString(undefined, {
    minimumFractionDigits: decimal ?? 0,
    maximumFractionDigits: decimal ?? 0,
  });
};

export const formatNumber = (amount: number) => {
  if (amount >= 1000 && amount < 10000) {
    return (
      (amount / 1000).toLocaleString("en", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }) + "k"
    );
  }
  else if (amount >= 10000) {
    return Math.round(amount / 1000).toLocaleString("en") + "k";
  }
  else {
    return amount.toLocaleString("en");
  }
};
// eslint-disable-next-line
export const formatOldFormatOfDateToAmericanFormat = (date: any) => {
  const formattedDate = DateTime.fromISO(date).toFormat("LL/dd/yyyy");

  if (formattedDate === "Invalid DateTime") {
    return "N/A";
  }

  return formattedDate;
};

// Formatting row values for table
export const formattingValue = (
  data: string | number,
  row: TableRow,
  col: TableHeader,
  isShouldTruncate = true,
) => {
  if (data === null) {
    return "";
  }

  // Handle capitalizing first letters
  if (col.capitalizeFirstLetterOfEachWord) {
    return capitalizeFirstLetterOfEachWord(data as string);
  }

  // Currency formatting
  if (col.currency) {
    return formatCurrency(data as number);
  }

  // Handle rounding and multiplication
  if (col.roundedWithTwoDecimalsAndMultiply) {
    return ((data as number) * 100)?.toFixed(2);
  }
  if (col.roundedToTwoDecimals) {
    return (data as number)?.toFixed(2);
  }
  if (col.roundedWithZeroDecimalsAndMultiply) {
    return ((data as number) * 100)?.toFixed(0);
  }
  if (col.roundedWithZeroDecimals) {
    return Number(data)?.toFixed(0);
  }

  // Handle comma formatting
  if (col.addComma) {
    return formatComma(data as number);
  }

  if (col.commasPlusDecimals) {
    return (+(data as number)?.toFixed(2)).toLocaleString();
  }

  // Date formatting
  if (col.value === "DATE" || col.date) {
    return formatOldFormatOfDateToAmericanFormat(
      row[col.value] || row["DATE"] || "Selected period",
    );
  }

  // Truncate string
  if (!!col.maxLength && isShouldTruncate) {
    return truncateString(data as string, col.maxLength);
  }

  // Null to empty string
  if (col.nullToEmptyString) {
    return data ?? "";
  }

  // Currency with two decimals
  if (col.currencyWithTwoDecimals) {
    if (!data) return "";
    return formatCurrencyWithTwoDecimals(+data as number);
  }

  if (col.currencyWithCommaAndDecimals) {
    return data
      ? `$${(+(data as number)?.toFixed(2))?.toLocaleString()}`
      : "";
  }

  return data || "";
};

export const truncateString = (str: string, maxLength: number) => {
  if (str?.length > maxLength) {
    return str.slice(0, maxLength - 1) + "...";
  }
  return str;
};

export const checkToParams = (params: requestParams) => {
  let findedParams = "";
  let isFirstParam = true;

  for (const key of Object.keys(params)) {
    if (params[key as keyof requestParams] !== "") {
      if (isFirstParam) {
        findedParams += `?${key}=${params[key as keyof requestParams]}`;
        isFirstParam = false;
      }
      else {
        findedParams += `&${key}=${params[key as keyof requestParams]}`;
      }
    }
  }

  return findedParams;
};

export const capitalizeFirstLetterOfEachWord = (string: string) => {
  if (!string) return "";
  return string
    .replace(/[_-]/g, " ")
    .toLowerCase()
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const convertToUTC = (date: string) => {
  if (!date) return;
  return DateTime.fromISO(date).toUTC().toFormat("yyyy-MM-dd HH:mm:ss");
};

export const convertDateFormatFromString = (dateStr: string) => {
  if (!dateStr) return "N/A";
  const [datePart, timePart] = dateStr.split(" ");
  const [year, month, day] = datePart.split("-");
  const newDateStr = `${month}/${day}/${year}`;

  return newDateStr;
};

export const convertDateFormatFromISO = (dateStr: string) => {
  return DateTime.fromISO(dateStr).toFormat("MM/dd/yyyy");
};
// eslint-disable-next-line
export const formatDateFromObject = (date: any) => {
  const dateObject = new Date(date);

  const year = dateObject.getFullYear();
  const month = ("0" + (dateObject.getMonth() + 1)).slice(-2);
  const day = ("0" + dateObject.getDate()).slice(-2);

  return `${year}-${month}-${day}`;
};
// eslint-disable-next-line
export const renameChannels = (channels: any, channel?: number | string) => {
  if (!channel) return "N/A";
  // eslint-disable-next-line
    return channels.find((item: any) => +item.id === +channel)?.name;
};

export const iconTypeForPrinters = (type: string) => {
  switch (type) {
    case "box":
      return "box";
    case "unit":
      return "file";
  }
};

export const textTypeForPrinters = (type: string) => {
  switch (type) {
    case "box":
      return "Case";
    case "unit":
      return "Unit";
    default:
      return "N/A";
  }
};

export const groupByLabelTitle = (
  printers: IUnusedPrinters[],
  usedPrinters: IUsedPrinter[] = [],
) => {
  const printerList = [...usedPrinters, ...printers];

  const labelMap = printerList.reduce((map, item) => {
    item.labels.forEach((label) => {
      const labelPrinters = map.get(label.name);

      const printer = Object.assign({}, item, {
        active: "active" in item ? item.active ?? false : false,
        is_default: "is_default" in label ? label.is_default : item.is_default,
      }) as IUsedPrinter;

      if (!labelPrinters) {
        const _label = Object.assign({}, label, {
          printers: [printer],
        }) as IUsedLabel & { printers: IUsedPrinter[] };

        map.set(label.name, _label);
      }
      else if (!labelPrinters.printers.find((printerItem) => printerItem.id === printer.id)) {
        labelPrinters.printers.push(printer);
      }
    });

    return map;
  }, new Map<string, IUsedLabel & { printers: IUsedPrinter[] }>());

  return Object.fromEntries(labelMap.entries());
};
// eslint-disable-next-line
export const getDateFromString = (dateString: any) => {
  const [, resultDate] = dateString.split(".");
  return resultDate;
};

export const formatToArrayForBulkSearch = (value: string) => {
  if (!value) return [];
  return value?.replace(/[\r\n;]+/g, ",")
    .split(",")
    .filter((v) => v.trim() !== "")
    .map((v) => v.trim());
};

export const formatToStringForBulkSearch = (value: string) => {
  if (!value) return "";
  return value?.replace(/[\r\n;]+/g, ",")
    .split(",")
    .filter((v) => v.trim() !== "")
    .map((v) => v.trim())
    .join(", ");
};
